import { useMemo, useState } from 'react';
import { ArrowLeft, ChevronDown, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useResources, useResourceRoles, useUpdateTicket, type Resource } from '@/api/hooks';

interface Props {
  ticketId: number;
  value: number | null;
  className?: string;
}

interface Pending {
  id: number;
  label: string;
}

export function AssignedResourcePicker({ ticketId, value, className }: Props) {
  const resources = useResources();
  const mutation = useUpdateTicket();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);

  const roles = useResourceRoles(pending?.id ?? null);

  const items = resources.data?.items ?? [];
  const current = items.find((r) => r.id === value);
  const currentLabel = current
    ? `${current.firstName} ${current.lastName}`.trim()
    : value == null
    ? 'Unassigned'
    : `#${value}`;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => {
      const name = `${r.firstName} ${r.lastName}`.toLowerCase();
      return name.includes(q) || (r.email ?? '').toLowerCase().includes(q);
    });
  }, [items, query]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery('');
      setPending(null);
    }
  };

  const selectPerson = (r: Resource) => {
    setPending({ id: r.id, label: `${r.firstName} ${r.lastName}`.trim() });
  };

  const unassign = () => {
    mutation.mutate(
      { id: ticketId, fields: { assignedResourceID: null, assignedResourceRoleID: null } },
      {
        onSuccess: () => { toast.success('Unassigned'); setOpen(false); },
        onError: (err) => toast.error('Failed to unassign', { description: err instanceof Error ? err.message : 'Unknown error' }),
      },
    );
  };

  const assignWithRole = (roleId: number) => {
    if (!pending) return;
    const { id, label } = pending;
    mutation.mutate(
      { id: ticketId, fields: { assignedResourceID: id, assignedResourceRoleID: roleId } },
      {
        onSuccess: () => { toast.success(`Assigned → ${label}`); setOpen(false); },
        onError: (err) => toast.error('Failed to assign', { description: err instanceof Error ? err.message : 'Unknown error' }),
      },
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger
        disabled={mutation.isPending}
        title={currentLabel}
        className={cn(
          'flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-sm',
          'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          mutation.isPending && 'opacity-60',
          value == null && 'text-muted-foreground',
          className,
        )}
      >
        {mutation.isPending ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : null}
        <span className="truncate">{currentLabel}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64 overflow-hidden p-0">
        {pending == null ? (
          // Step 1: pick a person
          <>
            <div className="p-2">
              <Input
                autoFocus
                placeholder="Search by name or email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-8"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
            <DropdownMenuSeparator className="my-0" />
            <div className="max-h-72 overflow-y-auto overflow-x-hidden py-1">
              <DropdownMenuItem onSelect={unassign} className="gap-2 text-muted-foreground">
                <UserX className="h-4 w-4 shrink-0" />
                <span className="truncate">Unassigned</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {resources.isLoading && (
                <DropdownMenuItem disabled>Loading resources…</DropdownMenuItem>
              )}
              {!resources.isLoading && filtered.length === 0 && (
                <DropdownMenuItem disabled>No matches</DropdownMenuItem>
              )}
              {filtered.map((r) => (
                <DropdownMenuItem
                  key={r.id}
                  onSelect={(e) => { e.preventDefault(); selectPerson(r); }}
                  className={cn('truncate', r.id === value && 'bg-accent text-accent-foreground')}
                >
                  <span className="truncate">{r.firstName} {r.lastName}</span>
                </DropdownMenuItem>
              ))}
            </div>
          </>
        ) : (
          // Step 2: pick a role for the selected person
          <>
            <div className="flex items-center gap-1.5 px-2 py-2">
              <button
                onClick={() => setPending(null)}
                className="rounded p-0.5 hover:bg-accent"
                aria-label="Back"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <span className="truncate text-sm font-medium">{pending.label}</span>
            </div>
            <DropdownMenuSeparator className="my-0" />
            <div className="px-2 py-1 text-xs text-muted-foreground">Select role</div>
            <div className="max-h-64 overflow-y-auto overflow-x-hidden py-1">
              {roles.isLoading && (
                <DropdownMenuItem disabled>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />Loading roles…
                </DropdownMenuItem>
              )}
              {!roles.isLoading && (roles.data?.items ?? []).length === 0 && (
                <DropdownMenuItem disabled>No roles found</DropdownMenuItem>
              )}
              {(roles.data?.items ?? []).map((role) => (
                <DropdownMenuItem key={role.id} onSelect={() => assignWithRole(role.id)}>
                  {role.name}
                </DropdownMenuItem>
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
